import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { BffGetLanguagesEndpoint, BffGetSubjectsEndpoint } from 'lingo2-api-models';
import { Country, Language, UserServiceCategory } from 'lingo2-models';
import { map, Observable, of, ReplaySubject, tap, zip } from 'rxjs';
import { LanguageService } from '../language.service';
import { BROWSER_SESSION_STORAGE } from './browser-storage';

const SUBJECTS_KEY = 'SUBJECTS_CACHE';
const LANGUAGES_KEY = 'LANGUAGES_CACHE';
const COUNTRIES_KEY = 'COUNTIES_CACHE';
const USCATEGORIES_KEY = 'USCATEGORIES_CACHE';

export type SubjectFromDictionary = BffGetSubjectsEndpoint.Response['data'][0];
export type LanguageFromDictionary = BffGetLanguagesEndpoint.Response['data'][0];

/**
 * Сервис со справочной информацией
 */
@Injectable({ providedIn: 'root' })
export class DictionaryService {
  private readonly http = inject(HttpClient);
  private readonly languageService = inject(LanguageService);
  storage = inject<Storage>(BROWSER_SESSION_STORAGE);

  private readonly bffUrl = environment.bff_url;
  private subjects$ = new ReplaySubject<SubjectFromDictionary[]>(1);
  private readonly subjectsIndex = new Map<number, SubjectFromDictionary>();
  private languages$ = new ReplaySubject<LanguageFromDictionary[]>(1);
  private readonly languagesIndex = new Map<number, LanguageFromDictionary>();
  private countries$ = new ReplaySubject<Country[]>(1);

  private readonly uscategoriesIndex = new Map<number, UserServiceCategory>();
  private uscategories$ = new ReplaySubject<UserServiceCategory[]>(1);

  private currentLanguage: Language;

  get subjects(): Observable<SubjectFromDictionary[]> {
    return this.subjects$.asObservable();
  }

  get languages(): Observable<LanguageFromDictionary[]> {
    return this.languages$.asObservable();
  }

  get uscategories(): Observable<UserServiceCategory[]> {
    return this.uscategories$.asObservable();
  }

  constructor() {
    this.languageService.language$.pipe(tap((currentLang) => (this.currentLanguage = currentLang))).subscribe();
    // TODO необходимо добавить проверку, не изменились ли данные на сервере
    // this.prepareCountries();
    zip(this.prepareLanguages(), this.prepareSubjects(), this.prepareUserServiceCategories()).subscribe();

    this.prepareIndex();
  }

  getSubjectById(id: number): SubjectFromDictionary | undefined {
    return this.subjectsIndex.get(id);
  }

  getLanguageById(id: number): LanguageFromDictionary | undefined {
    return this.languagesIndex.get(id);
  }

  private prepareSubjects(): Observable<boolean> {
    const getRequest = () =>
      this.getSubjects().pipe(
        map((subjects) => {
          this.storage.setItem(SUBJECTS_KEY, JSON.stringify(subjects));
          this.subjects$.next(subjects);
          return true;
        }),
      );
    return getRequest();

    // TODO local cache
    const dataFromStorage = this.storage.getItem(SUBJECTS_KEY);
    if (!dataFromStorage) {
      return getRequest();
    }

    const subjects = JSON.parse(dataFromStorage);

    if (subjects.length === 0) {
      return getRequest();
    }

    this.subjects$.next(subjects);
    return of(true);
  }

  private prepareLanguages(): Observable<boolean> {
    const getRequest = () =>
      this.getLanguages().pipe(
        map((languages) => {
          this.storage.setItem(LANGUAGES_KEY, JSON.stringify(languages));
          this.languages$.next(languages);
          return true;
        }),
      );
    return getRequest();
    // TODO local cache
    const dataFromStorage = this.storage.getItem(LANGUAGES_KEY);

    if (!dataFromStorage) {
      return getRequest();
    }

    const languages = JSON.parse(dataFromStorage);

    if (languages.length === 0) {
      return getRequest();
    }

    this.languages$.next(languages);
    return of(true);
  }

  private prepareUserServiceCategories(): Observable<boolean> {
    const getRequest = () =>
      this.getUserServiceCategories(this.currentLanguage?.code).pipe(
        map((categories) => {
          this.storage.setItem(USCATEGORIES_KEY, JSON.stringify(categories));
          this.uscategories$.next(categories);
          return true;
        }),
      );
    return getRequest();

    // TODO local cache
    const dataFromStorage = this.storage.getItem(SUBJECTS_KEY);
    if (!dataFromStorage) {
      return getRequest();
    }

    const subjects = JSON.parse(dataFromStorage);

    if (subjects.length === 0) {
      return getRequest();
    }

    this.subjects$.next(subjects);
    return of(true);
  }

  private prepareCountries() {
    const dataFromStorage = this.storage.getItem(COUNTRIES_KEY);
  }

  private prepareIndex() {
    this.subjects$.subscribe((subjects) => subjects.forEach((subject) => this.subjectsIndex.set(subject._id, subject)));
    this.languages$.subscribe((languages) =>
      languages.forEach((language) => this.languagesIndex.set(language._id, language)),
    );

    this.uscategories$.subscribe((categories) =>
      categories.forEach((category) => this.uscategoriesIndex.set(category.id, category)),
    );
  }

  private getSubjects(): Observable<BffGetSubjectsEndpoint.Response['data']> {
    return this.http[BffGetSubjectsEndpoint.method]<BffGetSubjectsEndpoint.Response>(
      `${this.bffUrl}/${BffGetSubjectsEndpoint.path}`,
      {
        observe: 'body',
      },
    ).pipe(map((response) => response.data));
  }

  private getLanguages(): Observable<BffGetLanguagesEndpoint.Response['data']> {
    return this.http[BffGetLanguagesEndpoint.method]<BffGetLanguagesEndpoint.Response>(
      `${this.bffUrl}/${BffGetLanguagesEndpoint.path}`,
      {
        observe: 'body',
      },
    ).pipe(map((response) => response.data));
  }

  private getUserServiceCategories(locale: string): Observable<UserServiceCategory[]> {
    const url = `${environment.content_url}/reference/user-service-categories`;
    const params = new HttpParams().set('locale', locale).set('v', environment.versions.gitCommitHash);
    return this.http.get<UserServiceCategory[]>(url, { params, observe: 'response' }).pipe(
      map((response) => response.body),
      // catchError(this.handleError),
    );
  }
}
